import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  HStack,
  Input,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Avatar,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
} from 'components-lib';
import { MdLogout } from 'react-icons/md';
import { BiSupport } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { KifaytiLogoIcon } from 'components/icons/Icons';
import ChatService from 'services/chatService';
import { SidebarResponsive } from 'components/sidebar/Sidebar';
import { useFocusMode } from 'contexts/FocusModeContext';
import axios from 'axios';

// Clean Navbar + Chat component
// - UI-only per-second tick to refresh time labels
// - Poll server every 10s while chat open
// - Optimistic message creation with temp id replacement on success
// - Minute-based timestamps for messages < 60 minutes (e.g., "0m", "5m")

export default function SimpleNavbarClean({ userName, routes = [] }) {
  const navigate = useNavigate();

  // Color mode values
  const navbarBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('navy.700', 'black');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const menuBg = useColorModeValue('white', 'navy.800');

  // Get user data from localStorage
  const tokenPayload = JSON.parse(localStorage.getItem('tokenPayload') || '{}');
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = tokenPayload?.role?.toLowerCase();
  const displayName = userName || userData.doctor || userData.firstname + " " + userData.lastname || tokenPayload.email || 'User';

  const toast = useToast();

  // Focus mode - get navigation tabs content from context
  const { navigationTabsContent } = useFocusMode();


  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // UI tick (1s) for time label refresh
  const [currentTime, setCurrentTime] = useState(new Date());
  const secondIntervalRef = useRef(null);

  // Poll every 10s while chat open
  const pollIntervalRef = useRef(null);

  // Track optimistic messages so server loads won't clobber them
  const pendingMessagesRef = useRef([]);

  // Message search
  const [messageSearchTerm, setMessageSearchTerm] = useState('');

  const { isOpen: isChatOpen, onOpen: onChatOpen, onClose: onChatClose } = useDisclosure();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Utility to escape regex special characters when highlighting search terms
  const escapeRegExp = (string) => {
    try {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    } catch (err) {
      return string;
    }
  };

  // Load history from server and merge pending optimistic messages
  const loadMessageHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    setHistoryError(null);
    try {
      const userInfo = ChatService.getCurrentUserInfo();
      if (!userInfo || !userInfo.userId) throw new Error('User not available');

      const resp = await ChatService.getMessageHistory(userInfo.userId, userInfo.userType, 1, 'admin');
      if (!resp) throw new Error('Empty response');

      let messagesData = [];
      if (Array.isArray(resp)) messagesData = resp;
      else if (resp.success === true) messagesData = resp.data || [];
      else if (resp.success === false) throw new Error(resp.message || 'Failed to load');
      else throw new Error('Unexpected response format');

      const formatted = messagesData.map(m => {
        try {
          return ChatService.formatMessageForDisplay(m);
        } catch (e) {
          return {
            id: m.id || Date.now(),
            message: m.message || '',
            senderName: m.sender_name || 'Unknown',
            timestamp: m.created_at ? new Date(m.created_at) : new Date(),
            isFromCurrentUser: m.sender_id === userInfo.userId,
          };
        }
      });

      // Merge with pending optimistic messages tracked locally
      const pending = Array.isArray(pendingMessagesRef.current) ? pendingMessagesRef.current : [];
      const merged = [...formatted];
      pending.forEach(p => {
        if (!merged.some(m => String(m.id) === String(p.id))) merged.push(p);
      });

      merged.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      setMessages(merged);
    } catch (error) {
      console.error('loadMessageHistory error', error);
      const errorMessage = error.message || 'Failed to load message history. Please try again.';
      setHistoryError(errorMessage);
      toast({
        title: "Error loading messages",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingHistory(false);
    }
  }, [toast]);

  // Keep pendingMessagesRef up-to-date
  useEffect(() => {
    pendingMessagesRef.current = messages.filter(m => m && m.isPending);
  }, [messages]);

  // Start 1s UI tick and 10s poller when chat opens
  useEffect(() => {
    if (isChatOpen) {
      loadMessageHistory();

      if (!secondIntervalRef.current) secondIntervalRef.current = setInterval(() => setCurrentTime(new Date()), 1000);
      if (!pollIntervalRef.current) pollIntervalRef.current = setInterval(() => loadMessageHistory(), 10000);
    } else {
      if (secondIntervalRef.current) { clearInterval(secondIntervalRef.current); secondIntervalRef.current = null; }
      if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    }

    return () => {
      if (secondIntervalRef.current) { clearInterval(secondIntervalRef.current); secondIntervalRef.current = null; }
      if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    };
  }, [isChatOpen, loadMessageHistory]);

  // Format timestamps: minutes for <60 minutes (0m, 5m), else time/day
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const now = currentTime || new Date();
    const messageDate = new Date(timestamp);
    if (isNaN(messageDate.getTime())) return '';
    // New behavior: always show human-readable time for message
    // - If message is today -> show time (e.g., 10:32 AM)
    // - If older -> show date (e.g., 05/08/2024) or include time for clarity
    const sameDay = messageDate.toDateString() === now.toDateString();
    if (sameDay) return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });

    const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
    if (messageDate.toDateString() === yesterday.toDateString()) {
      return `${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}`;
    }

    const diffInDays = Math.floor((now - messageDate) / (1000 * 60 * 60 * 24));
    if (diffInDays < 7) {
      // Within last week -> show weekday and time
      return `${messageDate.toLocaleDateString([], { weekday: 'long' })} ${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}`;
    }

    // Older: show full date and time
    return `${messageDate.toLocaleDateString()} ${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}`;
  };

  // Filter messages based on search term (case-insensitive)
  const filteredSorted = useMemo(() => {
    const term = messageSearchTerm.trim().toLowerCase();
    if (!term) return messages;
    return messages.filter(message =>
      (message.message || '').toLowerCase().includes(term) ||
      ((message.senderName || '') || '').toLowerCase().includes(term)
    );
  }, [messageSearchTerm, messages]);

  // Group messages by date label for UI sections (Today, Yesterday, or full date)
  const groupedMessages = useMemo(() => {
    const groups = [];
    for (const msg of filteredSorted) {
      try {
        const dt = new Date(msg.timestamp);
        const key = isNaN(dt.getTime()) ? 'Unknown' : dt.toDateString();
        const lastGroup = groups.length ? groups[groups.length - 1] : null;
        if (lastGroup && lastGroup.key === key) {
          lastGroup.messages.push(msg);
        } else {
          groups.push({ key, date: dt, messages: [msg] });
        }
      } catch (err) {
        // fallback: push into unknown group
        const lastGroup = groups.length ? groups[groups.length - 1] : null;
        if (lastGroup && lastGroup.key === 'Unknown') {
          lastGroup.messages.push(msg);
        } else {
          groups.push({ key: 'Unknown', date: null, messages: [msg] });
        }
      }
    }
    return groups;
  }, [filteredSorted]);

  // Optimistic send with enhanced error handling
  const handleSendMessage = async () => {
    if (!newMessage.trim() || isLoading || !newMessage.trim()) return;

    const messageToSend = newMessage.trim();

    // Validate message length
    if (messageToSend.length > 1000) {
      setSendError('Message is too long. Please keep it under 1000 characters.');
      return;
    }

    setIsLoading(true);
    setSendError(null);

    const tempId = `temp-${Date.now()}`;
    const tempMsg = {
      id: tempId,
      message: messageToSend,
      senderName: userName || 'You',
      senderType: 'user',
      timestamp: new Date(),
      isFromCurrentUser: true,
      isPending: true,
    };

    // Add message to local state for immediate UI update
    setMessages(prev => [...prev, tempMsg]);
    // ensure we scroll to new optimistic message
    setTimeout(() => scrollToBottom(), 60);
    setNewMessage('');

    try {
      const userInfo = ChatService.getCurrentUserInfo();
      const resp = await ChatService.sendMessageToAdmin(userInfo.userId, userInfo.userType, messageToSend);
      const serverMsg = resp && resp.data ? resp.data : resp;
      const formatted = ChatService.formatMessageForDisplay(serverMsg);
      setMessages(prev => prev.map(m => (m.id === tempId ? { ...formatted, isPending: false } : m)));
      // scroll to bottom after the real message arrives
      setTimeout(() => scrollToBottom(), 80);
    } catch (error) {
      console.error('send message error', error);
      // Remove the optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== tempId));
      // Restore the message text so user can retry
      setNewMessage(messageToSend);

      const errorMessage = error.message || "Failed to send message. Please check your connection and try again.";
      setSendError(errorMessage);

      toast({
        title: "Error sending message",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Auto scroll
  const scrollToBottom = (behavior = 'smooth') => {
    try {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({ top: messagesContainerRef.current.scrollHeight, behavior });
        return;
      }
      messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
    } catch (err) {
      // fallback: ignore
    }
  };

  useEffect(() => {
    // scroll when the messages list changes (or search changes)
    // Wait a tick to allow DOM to update
    const t = setTimeout(() => scrollToBottom(), 40);
    return () => clearTimeout(t);
  }, [groupedMessages, messageSearchTerm]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };
  useEffect(() => {
    if (!tokenPayload || !tokenPayload.logo) return;

    // Fetch logo as binary and convert to base64 data URL before storing
    axios.get(tokenPayload.logo, { responseType: 'blob' })
      .then((res) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(res.data);
      }))
      .then((base64Data) => {
        localStorage.setItem('clinic_logo_data', base64Data || 'null');
      })
      .catch((err) => {
        console.error('Error fetching logo data:', err);
      });
  }, [tokenPayload && tokenPayload.logo]);

  // Helper: convert data URL to Blob
  function dataURLToBlob(dataUrl) {
    if (!dataUrl || typeof dataUrl !== 'string') return null;
    const parts = dataUrl.split(',');
    if (parts.length < 2) return null;
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const base64 = parts[1];
    try {
      const byteString = atob(base64);
      const array = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) array[i] = byteString.charCodeAt(i);
      return new Blob([array], { type: mime });
    } catch (err) {
      return null;
    }
  }

  // State for the final image src we will use in <img>
  const [clinicLogoSrc, setClinicLogoSrc] = useState(null);
  const clinicLogoObjectUrlRef = useRef(null);

  useEffect(() => {
    // Read freshest value from localStorage (or tokenPayload fallback)
    const stored = localStorage.getItem('clinic_logo_data') || localStorage.getItem('clinic_logo') || localStorage.getItem('logo') || tokenPayload.logo || '';

    // Cleanup previous object URL if any
    if (clinicLogoObjectUrlRef.current) {
      try { URL.revokeObjectURL(clinicLogoObjectUrlRef.current); } catch (e) { /* ignore */ }
      clinicLogoObjectUrlRef.current = null;
    }

    if (!stored || stored === 'null') {
      setClinicLogoSrc(null);
      return;
    }

    const trimmed = String(stored).trim();

    // Already a data URL
    if (trimmed.startsWith('data:')) {
      setClinicLogoSrc(trimmed);
      return;
    }

    // A full http(s) URL -> use directly
    if (/^https?:\/\//i.test(trimmed)) {
      setClinicLogoSrc(trimmed);
      return;
    }

    // Otherwise assume it's raw base64 (no data: prefix)
    // Create a data URL and convert to an object URL (safer for very large images)
    const maybeDataUrl = `data:image/png;base64,${trimmed}`;
    const blob = dataURLToBlob(maybeDataUrl);
    if (blob) {
      try {
        const obj = URL.createObjectURL(blob);
        clinicLogoObjectUrlRef.current = obj;
        setClinicLogoSrc(obj);
        return;
      } catch (err) {
        // Fallback to using data URL string directly
        setClinicLogoSrc(maybeDataUrl);
        return;
      }
    }

    // If blob conversion failed, as a last resort try the data URL string
    setClinicLogoSrc(maybeDataUrl);

    return () => {
      if (clinicLogoObjectUrlRef.current) {
        try { URL.revokeObjectURL(clinicLogoObjectUrlRef.current); } catch (e) { /* ignore */ }
        clinicLogoObjectUrlRef.current = null;
      }
    };
  }, [tokenPayload && tokenPayload.logo]);


  return (
    <Box bg={navbarBg} px={4} maxW="100%" py={2} borderBottomWidth={1} borderColor="gray.200">
      <Flex alignItems="center" justifyContent="space-between" position="relative">
        <HStack spacing={4}>
          {userRole === 'admin' &&
            <SidebarResponsive routes={routes} />
          }
          <Flex align="start" gap={4}


          >
            <KifaytiLogoIcon h='44px' w='44px' />
            <Flex align="center" gap={4}>
              <Text
                // className="bg-gradient-to- from-[#05C4BB] to-[#FF4A2E]  px-3 py-2 rounded-md"
                py={1}
                fontSize={{ base: "lg", md: "xl" }}
                fontWeight="bold"
                color={useColorModeValue("gray.800", "white")}
              >
                Kifayti Health
              </Text>
            </Flex>
          </Flex>
        </HStack>

        {/* Center Navigation Tabs in Focus Mode */}
        {/* Desktop / XL: show full tabs in center */}
        <Box
          position="absolute"
          left="50%"
          transform="translateX(-50%)"
          display={{ base: "none", xl: "block" }}
          marginRight="40px"
        >
          {navigationTabsContent}
        </Box>

        {/* Tablet / Phone: show a compact select-style menu */}
        {/* <Box
          position="absolute"
          left="50%"
          transform="translateX(-50%)"
          display={{ base: "block", md: "block", xl: "none" }}
          marginRight="40px"
        >
          <Menu isLazy>
            <MenuButton
              as={Button}
              variant="outline"
              size="sm"
              borderRadius="md"
              px={4}
              py={2}
              bg={menuBg}
              borderColor={borderColor}
              _hover={{ bg: useColorModeValue('gray.50', 'navy.700') }}
            >
              Navigate
            </MenuButton>
            <MenuList bg={menuBg} border="1px solid" borderColor={borderColor} minW="xs">
              {routes && routes.length > 0 ? routes.map((r, idx) => (
                <MenuItem
                  key={idx}
                  onClick={() => {
                    try {
                      const path = (r.layout ? `${r.layout}${r.path}` : r.path) || '/';
                      navigate(path);
                    } catch (err) {
                      // ignore navigation errors
                    }
                  }}
                >
                  {r.name || r.displayName || r.title || 'Link'}
                </MenuItem>
              )) : (
                <MenuItem isDisabled>
                  {navigationTabsContent || 'No navigation'}
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        </Box> */}

        <Flex alignItems="center" gap={3}>
          <Button
            aria-label="Open support chat"
            borderRadius="full"
            variant="ghost"
            size="sm"
            gap={2}
            textColor={textColor}
            fontWeight="1000"
            border="0px"
            // _hover={{ bg: "blue.100" }}
            onClick={onChatOpen}
          >
            <Box as={BiSupport} boxSize="18px" mr={2} />
            Support
          </Button>

          <Flex align="center" gap={4}>
            {/* User role badge */}
            {/* <Box
              px={3}
              py={1}
              bg="teal.50"
              color="teal.800"
              borderRadius="full"
              fontSize="sm"
              fontWeight="bold"
              display={{ base: "none", md: "block" }}
              border="1px solid"
              borderColor="teal.100"
            >
              {displayRole}
            </Box> */}

            {/* User menu */}
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                size="sm"
                border="0px"
                // leftIcon={logo ? null : <Avatar size="sm" name={displayName} mr={2} />}
                minW="auto"
              // _hover={{ bg: useColorModeValue('gray.200', 'navy.700') }}
              >
                {
                  clinicLogoSrc ? (
                    <img src={clinicLogoSrc} alt="Profile" style={{ width: "32px", height: "32px", borderRadius: "50%", marginRight: "8px" }} />
                  ) : (
                    <Avatar size="sm" name={displayName} mr={2} />
                  )
                }
                <Text
                  display={{ base: "none", md: "block" }}
                  maxW="150px"
                  isTruncated
                  textColor={textColor}
                  fontWeight="1000"
                  textTransform="uppercase"
                >
                  {displayName}
                </Text>
              </MenuButton>
              <MenuList bg={menuBg} border="1px solid" borderColor={borderColor}>
                <MenuItem isDisabled>
                  <Box>
                    <Text fontWeight="bold">{displayName}</Text>
                    {/* <Text fontSize="sm" color="black">
                      {displayRole}
                    </Text> */}
                  </Box>
                </MenuItem>
                <MenuDivider />
                <MenuItem
                  icon={<MdLogout />}
                  onClick={handleLogout}
                  color="red.600"
                  _hover={{ bg: "red.100" }}
                  fontWeight="bold"
                >
                  Logout
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>


          {/* <IconButton aria-label="user" icon={<MdPerson />} variant="ghost" onClick={() => { }} /> */}
          {/* <Button variant="ghost" leftIcon={<MdLogout />} onClick={handleLogout}>Logout</Button> */}
        </Flex>
      </Flex>

      <Modal isOpen={isChatOpen} onClose={onChatClose} size="xl">
        <ModalOverlay />
        <ModalContent maxH="80vh" display="flex" flexDirection="column">
          <ModalHeader>
            <VStack spacing={3} align="stretch">
              <Flex alignItems="center">
                <Avatar name="Support" size="sm" mr={3} bg="blue.500" />
                <Box>
                  <Text fontSize="lg" fontWeight="bold">
                    Support Team
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Support will get back to you in next 24-48 hours
                  </Text>
                </Box>
              </Flex>

              {/* Message Search */}
              <Flex gap={3} align="center">
                <Text fontSize="sm" fontWeight="medium" minW="fit-content">
                  Search messages:
                </Text>
                <Input
                  placeholder="Search within this conversation..."
                  value={messageSearchTerm}
                  onChange={(e) => setMessageSearchTerm(e.target.value)}
                  size="sm"
                  flex="1"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setMessageSearchTerm('')}
                >
                  Clear
                </Button>
              </Flex>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody flex="1" p={4} minH={0}>
            {historyError ? (
              <Flex justify="center" align="center" h="400px" p={4}>
                <Alert
                  status="error"
                  borderRadius="lg"
                  maxW="450px"
                  boxShadow="md"
                  bg="red.50"
                  borderColor="red.200"
                  borderWidth="1px"
                >
                  <AlertIcon color="red.500" />
                  <Box flex="1">
                    <AlertTitle fontSize="md" mb={2} color="red.800">
                      Unable to Load Messages
                    </AlertTitle>
                    <AlertDescription fontSize="sm" mb={4} color="red.700">
                      {historyError}
                    </AlertDescription>
                    <HStack spacing={3}>
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="solid"
                        onClick={loadMessageHistory}
                        isLoading={isLoadingHistory}
                        loadingText="Retrying..."
                        leftIcon={!isLoadingHistory ? <BiSupport /> : undefined}
                      >
                        Try Again
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => {
                          setHistoryError(null);
                          setMessages([]);
                        }}
                      >
                        Dismiss
                      </Button>
                    </HStack>
                  </Box>
                </Alert>
              </Flex>
            ) : messages.length > 0 ? (
              <Box display="flex" flexDirection="column" gap={3} maxH="60vh" overflowY="auto" pb={4} px={2} ref={messagesContainerRef}>
                {isLoadingHistory && (
                  <Flex justify="center" mt={2}>
                    <HStack spacing={2}>
                      <Spinner size="sm" color="gray.500" />
                      <Text fontSize="xs" color="gray.500">Updating messages...</Text>
                    </HStack>
                  </Flex>
                )}
                {groupedMessages.length === 0 ? (
                  <Flex justify="center" align="center" h="100%">
                    <VStack spacing={4}>
                      <Box
                        p={6}
                        borderRadius="full"
                        bg="gray.100"
                        color="gray.400"
                      >
                        <BiSupport size="32px" />
                      </Box>
                      <VStack spacing={2}>
                        <Text color="gray.600" fontSize="lg" fontWeight="500">
                          {messageSearchTerm.trim() ? 'No messages match your search' : 'No messages yet'}
                        </Text>
                        <Text color="gray.500" fontSize="sm" textAlign="center" maxW="200px">
                          {messageSearchTerm.trim() ? 'Try adjusting your search terms' : 'Start a conversation with support'}
                        </Text>
                      </VStack>
                    </VStack>
                  </Flex>
                ) : (
                  groupedMessages.map(group => (
                    <React.Fragment key={group.key}>
                      <Flex justify="center" my={3}>
                        <Text
                          fontSize="xs"
                          color="gray.600"
                          bg="gray.100"
                          px={4}
                          py={2}
                          borderRadius="full"
                          fontWeight="500"
                          border="1px solid"
                          borderColor="gray.200"
                        >
                          {(() => {
                            try {
                              if (!group.date) return 'Unknown date';
                              const messageDate = new Date(group.date);
                              const today = new Date();
                              const yesterday = new Date(today);
                              yesterday.setDate(yesterday.getDate() - 1);

                              if (messageDate.toDateString() === today.toDateString()) return 'Today';
                              if (messageDate.toDateString() === yesterday.toDateString()) return 'Yesterday';

                              return messageDate.toLocaleDateString([], {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              });
                            } catch (error) {
                              console.error('Error formatting date separator:', error);
                              return 'Unknown date';
                            }
                          })()}
                        </Text>
                      </Flex>

                      {group.messages.map((message) => (
                        <Box
                          key={message.id}
                          bg={message.isFromCurrentUser ? "brand.500" : "white"}
                          color={message.isFromCurrentUser ? "white" : "gray.800"}
                          p={3}
                          borderRadius="16px"
                          maxW={['85%', '70%']}
                          alignSelf={message.isFromCurrentUser ? "flex-end" : "flex-start"}
                          boxShadow={message.isFromCurrentUser ? "md" : "sm"}
                          position="relative"
                          opacity={message.isPending ? 0.85 : 1}
                          borderWidth="1px"
                          borderColor={message.isFromCurrentUser ? "brand.600" : "gray.200"}
                          transition="all 0.12s ease"
                          _hover={{ transform: "translateY(-1px)", boxShadow: message.isFromCurrentUser ? "lg" : "md" }}
                          mb={1}
                        >
                          <HStack justify="space-between" mb={1} w="100%">
                            <Text
                              fontSize="xs"
                              color={
                                message.isFromCurrentUser ? "whiteAlpha.900" : "gray.600"
                              }
                              fontWeight="600"
                            >
                              {message.isFromCurrentUser ? 'You' : (message.senderName || 'Support Team')}
                            </Text>
                            <Text
                              fontSize="xs"
                              color={
                                message.isFromCurrentUser ? "whiteAlpha.700" : "gray.500"
                              }
                              fontWeight="400"
                              flexShrink={0}
                            >
                              {formatMessageTime(message.timestamp)}
                            </Text>
                          </HStack>
                          <Text fontSize="sm" lineHeight="1.45" wordBreak="break-word" whiteSpace="pre-wrap" color={message.isFromCurrentUser ? "white" : "gray.800"}>
                            {messageSearchTerm.trim() ? (
                              (() => {
                                const escaped = escapeRegExp(messageSearchTerm.trim());
                                const parts = (message.message || '').split(new RegExp(`(${escaped})`, 'gi'));
                                return parts.map((part, i) =>
                                  part.toLowerCase() === messageSearchTerm.toLowerCase()
                                    ? <Box as="span" key={i} bg={message.isFromCurrentUser ? "whiteAlpha.200" : "yellow.200"} px={1} borderRadius="sm">{part}</Box>
                                    : part
                                );
                              })()
                            ) : (
                              message.message
                            )}
                          </Text>
                          {message.isFromCurrentUser && (
                            <HStack justify="flex-end" mt={2} spacing={2}>
                              {message.isPending && (
                                <Spinner size="xs" color="whiteAlpha.600" thickness="2px" />
                              )}
                              <Text
                                fontSize="xs"
                                color="whiteAlpha.800"
                                fontWeight="500"
                              >
                                {message.isPending ? "Sending..." : (message.isRead ? "Read" : "Sent")}
                              </Text>
                            </HStack>
                          )}
                        </Box>
                      ))}
                    </React.Fragment>
                  ))
                )}
                <Box ref={messagesEndRef} />
              </Box>
            ) : isLoadingHistory ? (
              <Flex justify="center" align="center" h="400px">
                <VStack spacing={4}>
                  <Spinner
                    size="xl"
                    color="brand.500"
                    thickness="4px"
                    speed="0.65s"
                  />
                  <VStack spacing={2}>
                    <Text color="gray.600" fontSize="md" fontWeight="500">
                      Loading message history...
                    </Text>
                    <Text color="gray.500" fontSize="sm">
                      Please wait while we fetch your conversation
                    </Text>
                  </VStack>
                </VStack>
              </Flex>
            ) : (
              <Flex justify="center" align="center" h="400px">
                <VStack spacing={4}>
                  <Box
                    p={6}
                    borderRadius="full"
                    bg="gray.100"
                    color="gray.400"
                  >
                    <BiSupport size="32px" />
                  </Box>
                  <VStack spacing={2}>
                    <Text color="gray.600" fontSize="lg" fontWeight="500">
                      No messages yet
                    </Text>
                    <Text color="gray.500" fontSize="sm" textAlign="center" maxW="200px">
                      Start a conversation with support
                    </Text>
                  </VStack>
                </VStack>
              </Flex>
            )}
          </ModalBody>

          <ModalFooter p={4}>
            <VStack w="100%" spacing={4}>
              <VStack w="100%" spacing={3}>
                <HStack w="100%" spacing={3}>
                  <Input
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 1000) {
                        setNewMessage(value);
                        if (sendError) {
                          setSendError(null);
                        }
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && !isLoading && newMessage.trim()) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={isLoading || isLoadingHistory}
                    bg="white"
                    borderColor={newMessage.length > 900 ? "orange.300" : "gray.300"}
                    borderWidth="2px"
                    _hover={{
                      borderColor: newMessage.length > 900 ? "orange.400" : "gray.400",
                    }}
                    _focus={{
                      borderColor: newMessage.length > 900 ? "orange.500" : "brand.500",
                      boxShadow: `0 0 0 1px var(--chakra-colors-${newMessage.length > 900 ? 'orange' : 'brand'}-500)`,
                    }}
                    _disabled={{
                      opacity: 0.6,
                      cursor: "not-allowed"
                    }}
                    size="md"
                  />
                  <Button
                    colorScheme="brand"
                    onClick={handleSendMessage}
                    isLoading={isLoading}
                    loadingText="Sending"
                    disabled={isLoading || isLoadingHistory || !newMessage.trim()}
                    minW="90px"
                    size="md"
                    borderRadius="md"
                    fontWeight="600"
                  >
                    Send
                  </Button>
                </HStack>

                {/* Status bar with character counter */}
                <HStack w="100%" justify="space-between" fontSize="xs" minH="16px">
                  <HStack spacing={2}>
                    <Text color={newMessage.length > 900 ? "orange.500" : "gray.500"}>
                      {newMessage.length}/1000 characters
                    </Text>
                    {newMessage.length > 950 && (
                      <Text color="orange.600" fontWeight="500">
                        • {1000 - newMessage.length} remaining
                      </Text>
                    )}
                  </HStack>

                  <HStack spacing={2}>
                    {sendError && (
                      <HStack spacing={1}>
                        <Box w="6px" h="6px" bg="red.500" borderRadius="full" />
                        <Text color="red.500" fontWeight="500">
                          Send failed
                        </Text>
                      </HStack>
                    )}
                    {historyError && !sendError && (
                      <HStack spacing={1}>
                        <Box w="6px" h="6px" bg="orange.500" borderRadius="full" />
                        <Text color="orange.500" fontWeight="500">
                          Connection issue
                        </Text>
                      </HStack>
                    )}
                    {!historyError && !sendError && !isLoadingHistory && (
                      <HStack spacing={1}>
                        <Box w="6px" h="6px" bg="green.500" borderRadius="full" />
                        <Text color="green.600" fontWeight="500">
                          Connected
                        </Text>
                      </HStack>
                    )}
                  </HStack>
                </HStack>
              </VStack>

              {/* Loading indicator for message sending */}
              {isLoading && (
                <HStack
                  spacing={3}
                  w="100%"
                  justify="center"
                  p={3}
                  bg="blue.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  <Spinner size="sm" color="brand.500" thickness="3px" />
                  <VStack spacing={0}>
                    <Text fontSize="sm" color="brand.600" fontWeight="600">
                      Sending message...
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Please wait
                    </Text>
                  </VStack>
                </HStack>
              )}
            </VStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
